// Modal lookup picker for columns with a `lookup` definition
// (TABLE(DISPLAY_COL, DATA_COL)). Lets the user search the lookup table
// and click a row to select a value. When the source transaction is
// available in the project, also offers a "+ Add" affordance that
// opens an inline Add form for the lookup table itself — saves a new
// row, refetches, and auto-selects it back into the field that
// triggered the lookup.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Save, Search, X } from 'lucide-react'
import { useTxnRuntime } from './context'
import type { LookupRow } from './contracts'
import type {
  LookupFilterRules, SqlModel, TransactionModel,
} from './types'
import { compileLookupFilter } from './types'
import { useKeyboardShortcuts, useShortcutLayer } from './keyboard'
import {
  auditTokenOf, columnFormKey, isAutoGenerate, isFunctionCallDefault,
  mainSqlModel, parseApiError, projectVarOf, visibleColumns,
} from './utils'
import FormRenderer, { fieldAnchorId } from './FormRenderer'
import { Z_LOOKUP } from '../layers'
import { tw } from '../testHooks'

interface Props {
  open: boolean
  project: string
  objName: string
  lookup: string
  fieldLabel: string
  // Custom window title. Falls back to ``Select <fieldLabel>`` when empty.
  title?: string
  // Transaction object opened by the "+ Add" button to create a new master
  // record (then auto-selected). When set it drives Add directly; otherwise
  // Add falls back to resolving the owning transaction from the lookup spec.
  addObject?: string
  // SQL mode: which output column is the stored value (name or 1-based
  // position). Forwarded to the backend so it picks the right column.
  dataCol?: string
  onClose: () => void
  onSelect: (row: LookupRow) => void
  // Optional WHERE-clause filter (with {form.X} / {header.X} / {lit.N}
  // placeholders) and the values they bind to. Empty/undefined means no
  // filter is applied. When ``filterRules`` is supplied, it is compiled
  // into SQL + a literal map internally and takes precedence over the
  // raw ``filter`` string.
  filter?: string
  filterRules?: LookupFilterRules
  // Advanced (raw SQL) lookup. When non-empty, the modal sends this full
  // SELECT instead of the declarative ``lookup`` spec + filter. Form/header
  // values still flow so {form.X} / {header.X} placeholders bind.
  lookupSql?: string
  // The field this picker fills (its column name), sent with an SQL lookup so
  // an imported Vision pophelp binds its own ``?`` apart from other fields'.
  field?: string
  // The field a reused (inherited) Vision pophelp was written for — its refs
  // to that field mean this one.
  inheritedField?: string
  formValues?: Record<string, unknown>
  headerValues?: Record<string, unknown>
  // Whether to offer the "+ Add new master" affordance. Defaults to true
  // (transaction forms). Visual/dashboard CRITERIA pass false — a read-only
  // filter shouldn't create master records, and the host runtime there only
  // provides ``lookup`` (no save/deploy/lookupSource), so Add must stay hidden.
  allowAdd?: boolean
  // ── Multi-select (IN-operator criteria) ──
  // When true, clicking a row TOGGLES it instead of closing; an "Apply"
  // footer hands the full selection back via ``onSelectMany``. ``onSelect``
  // is not called in this mode.
  multi?: boolean
  // Values already chosen (pre-ticked when the picker opens).
  selectedValues?: Array<string | number>
  onSelectMany?: (rows: LookupRow[]) => void
  // What the person typed in the field before opening the picker. An SQL
  // lookup opens with it in the search box: an imported Vision pophelp reads
  // its own field's ``?`` from the search text (Vision bound the typed value),
  // so "type 3 characters, press F2" finds rows instead of hitting the
  // pophelp's "enter at least N characters" guard row. Ignored for
  // declarative and multi-select lookups.
  initialQuery?: string
}

// Pull the table name + data column out of `TABLE(DISPLAY, DATA)`.
// Returns null when the spec is malformed — caller suppresses the Add
// button in that case rather than guess.
function parseLookupSpec(spec: string): { table: string; data: string } | null {
  const m = (spec || '').trim().match(
    /^([A-Za-z_][A-Za-z0-9_]*)\s*\(\s*([A-Za-z_][A-Za-z0-9_]*)\s*,\s*([A-Za-z_][A-Za-z0-9_]*)\s*\)\s*$/,
  )
  if (!m) return null
  return { table: m[1], data: m[3] }
}

export default function LookupModal({
  open, project, objName, lookup, fieldLabel, title, addObject, dataCol,
  onClose, onSelect,
  filter, filterRules, lookupSql, field, inheritedField, formValues, headerValues, allowAdd = true,
  multi = false, selectedValues, onSelectMany, initialQuery,
}: Props) {
  const rt = useTxnRuntime()
  const sqlMode = (lookupSql || '').trim().length > 0
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState<LookupRow[]>([])
  // Ordered output columns for SQL multi-column lookups (empty → render the
  // legacy Display | Value two-column table). ``dataKey`` is the resolved
  // value column so the grid can flag it.
  const [columns, setColumns] = useState<string[]>([])
  const [dataKey, setDataKey] = useState('')
  const [total, setTotal] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const debounceRef = useRef<number | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const cardRef = useRef<HTMLDivElement | null>(null)
  // Register as the topmost shortcut layer while open so the editor / row form
  // underneath (whose card DOM-contains this overlay) doesn't also handle keys.
  useShortcutLayer(open)
  // Keyboard-highlighted row (arrow-key navigation). Re-anchored to the first
  // row whenever the result set changes.
  const [activeIndex, setActiveIndex] = useState(0)

  // Parsed once per `lookup` change. Used to drive the table → owning
  // transaction resolve and to find the DATA column for auto-select.
  const spec = useMemo(() => parseLookupSpec(lookup), [lookup])

  // Compile the structured filter when present — otherwise fall back to
  // the raw SQL string. Result is memoised so each render doesn't
  // re-allocate the literals map.
  const compiled = useMemo(() => {
    // SQL mode owns its own WHERE clause — the declarative filter is ignored.
    if (sqlMode) return { sql: '', literals: {} as Record<string, unknown> }
    if (filterRules) return compileLookupFilter(filterRules)
    return { sql: (filter || '').trim(), literals: {} as Record<string, unknown> }
  }, [filter, filterRules, sqlMode])

  // Serialise the filter inputs for the useCallback dep array — these
  // are objects/strings that might be a fresh reference each render.
  const filterSig = useMemo(
    () => JSON.stringify({
      sql: compiled.sql, lits: compiled.literals, lookupSql, dataCol, field,
      inheritedField, formValues, headerValues,
    }),
    [compiled, lookupSql, dataCol, field, inheritedField, formValues, headerValues],
  )

  const fetchRows = useCallback(async (q: string) => {
    setBusy(true)
    setError(null)
    try {
      const res = await rt.lookup(project, objName, lookup, q, 100, {
        filter: compiled.sql || undefined,
        formValues,
        headerValues,
        literalValues: compiled.sql ? compiled.literals : undefined,
        lookupSql: sqlMode ? lookupSql : undefined,
        lookupSqlDataCol: sqlMode ? dataCol : undefined,
        lookupField: sqlMode ? field : undefined,
        lookupInheritedField: sqlMode ? inheritedField : undefined,
      })
      setRows(res.rows)
      setActiveIndex(0)
      setTotal(res.total)
      // Show every selected column as its own grid column only when there
      // are at least two — a single column is clearer as Display | Value.
      setColumns((res.columns || []).length > 1 ? res.columns! : [])
      setDataKey(res.data_col || '')
    } catch (err) {
      const { message } = parseApiError(err)
      setError(message)
      setRows([])
      setColumns([])
      setDataKey('')
      setTotal(0)
    } finally {
      setBusy(false)
    }
    // filterSig captures filter/formValues/headerValues — depending on
    // them directly would re-create fetchRows on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, objName, lookup, filterSig])

  // Read through a ref so the field's value is taken at the moment the picker
  // opens, without re-running the open effect (and resetting the search box)
  // when the form re-renders underneath it.
  const seedRef = useRef('')
  seedRef.current = sqlMode && !multi ? String(initialQuery ?? '').trim() : ''

  // Reset + initial load when modal opens
  useEffect(() => {
    if (!open) return
    const seed = seedRef.current
    setQuery(seed)
    fetchRows(seed).catch(() => undefined)
    // focus search input
    window.setTimeout(() => inputRef.current?.focus(), 60)
  }, [open, fetchRows])

  // Debounced search on query change
  useEffect(() => {
    if (!open) return
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => {
      fetchRows(query).catch(() => undefined)
    }, 250)
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open])

  // ── Add-new-row sub-form state ──
  // `addState.kind` drives what we show: 'idle' (no add form), 'resolving'
  // (asking the backend which transaction owns this lookup table), 'open'
  // (form is visible). `unsupported` is captured once when the resolver
  // can't find an owning transaction so we don't ping the backend on every
  // open of the lookup.
  type AddState =
    | { kind: 'idle' }
    | { kind: 'resolving' }
    | { kind: 'unsupported'; reason: string }
    | {
        kind: 'open'
        sourceObjName: string
        sm: SqlModel
        values: Record<string, unknown>
        errors: Record<string, string>
        banner: string | null
        saving: boolean
      }
  const [addState, setAddState] = useState<AddState>({ kind: 'idle' })

  // ESC closes the lookup; while the Add sub-form is open, ESC closes
  // that instead so the user can back out of Add without dismissing the
  // whole lookup.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (addState.kind === 'open') {
        e.stopPropagation()
        setAddState({ kind: 'idle' })
        return
      }
      onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, addState.kind])

  // Reset Add state when the modal is closed (so reopening on a
  // different field starts clean).
  useEffect(() => {
    if (!open) setAddState({ kind: 'idle' })
  }, [open])

  // ── Multi-select state (IN criteria) ──
  // Keyed by String(data). Seeded from the caller's current values so the
  // picker opens pre-ticked; keys with no fetched row yet keep a synthesized
  // {data, display} so Apply never loses an off-page selection.
  const [multiSel, setMultiSel] = useState<Map<string, LookupRow>>(new Map())
  useEffect(() => {
    if (!open || !multi) return
    const m = new Map<string, LookupRow>()
    for (const v of selectedValues || []) {
      const key = String(v)
      if (key) m.set(key, { data: v, display: key })
    }
    setMultiSel(m)
    // Seed once per open — live edits flow through toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, multi])

  const toggleRow = (row: LookupRow) => {
    const key = String(row.data ?? '')
    setMultiSel((prev) => {
      const next = new Map(prev)
      if (next.has(key)) next.delete(key)
      else next.set(key, row)
      return next
    })
  }

  const applyMulti = () => {
    try {
      onSelectMany?.(Array.from(multiSel.values()))
      onClose()
    } catch (err) {
      rt.notify.error(`Could not apply selection: ${(err as Error).message}`)
    }
  }

  // Select a row and close (shared by mouse click and Enter). Multi mode
  // toggles instead — Apply hands the set back.
  const pickRow = (row: LookupRow) => {
    if (multi) { toggleRow(row); return }
    try {
      onSelect(row)
      onClose()
    } catch (err) {
      rt.notify.error(`Could not apply selection: ${(err as Error).message}`)
    }
  }
  const isTicked = (row: LookupRow) => multiSel.has(String(row.data ?? ''))

  // Arrow-key navigation, Enter to pick the highlighted row, Ctrl+F to jump
  // back to the search box. Disabled while the inline Add sub-form is open
  // (focus belongs to its fields then, not the picker list).
  useKeyboardShortcuts(
    [
      {
        combo: 'arrowdown', allowInInput: true,
        run: () => setActiveIndex(i => Math.min(rows.length - 1, i + 1)),
      },
      {
        combo: 'arrowup', allowInInput: true,
        run: () => setActiveIndex(i => Math.max(0, i - 1)),
      },
      {
        combo: 'enter', allowInInput: true,
        run: () => { const r = rows[activeIndex]; if (r) pickRow(r) },
      },
      { combo: 'ctrl+f', run: () => inputRef.current?.focus() },
    ],
    { enabled: open && addState.kind !== 'open', scopeRef: cardRef },
  )

  // Keep the highlighted row scrolled into view as the user arrows through.
  useEffect(() => {
    if (!open) return
    const el = cardRef.current?.querySelector(`[data-lookup-row="${activeIndex}"]`)
    if (el) (el as HTMLElement).scrollIntoView({ block: 'nearest' })
  }, [activeIndex, open])

  // What the "+ Add" button targets: the configured add-object wins, else
  // we fall back to the lookup spec's table (declarative mode).
  const addTarget = (addObject || '').trim()
  const addLabel = addTarget || spec?.table || 'record'

  const startAdd = useCallback(async () => {
    if (!addTarget && !spec) return
    setAddState({ kind: 'resolving' })
    try {
      const res = await rt.lookupSource(
        project,
        addTarget ? { objName: addTarget } : { table: spec!.table },
      )
      const m = (res.transaction as { transaction?: TransactionModel } | TransactionModel | null)
      const txnModel = (
        (m as { transaction?: TransactionModel })?.transaction
        ?? (m as TransactionModel)
      ) as TransactionModel | null
      const sm = txnModel ? mainSqlModel(txnModel) : null
      if (!sm) {
        setAddState({
          kind: 'unsupported',
          reason: `${addLabel} doesn't have a usable form.`,
        })
        return
      }
      // Seed defaults from the model's column metadata. We skip audit /
      // project-variable / auto-generate columns because those are filled
      // server-side at save time; surfacing them would just confuse the
      // user with non-editable placeholders.
      const initial: Record<string, unknown> = {}
      for (const col of visibleColumns(sm)) {
        const dv = (col.default_value || '').trim()
        if (!dv) continue
        if (auditTokenOf(col) || projectVarOf(col) || isAutoGenerate(col)) continue
        // Function-call defaults (`logged_in_user()`, `today()`, …) are
        // resolved server-side at INSERT — never seed the literal token.
        if (isFunctionCallDefault(col)) continue
        initial[columnFormKey(col)] = dv
      }
      // Make sure the lookup-table schema is deployed before we let the
      // user click Save — otherwise the first save would 422 on a missing
      // preview table. Deploy is idempotent so the round-trip is cheap.
      try {
        await rt.deploy(project, res.obj_name)
      } catch (err) {
        const { message, status } = parseApiError(err)
        // 403 = this caller may not deploy (a cross-tenant runtime guest holds
        // project.run_app, never project.live_preview). Deploy is the author's
        // maintenance op, so the master they are adding into is already
        // deployed — carry on rather than blocking Add. Other failures still
        // stop here, because then the table really may be missing.
        if (status !== 403) {
          setAddState({ kind: 'unsupported', reason: `Deploy failed: ${message}` })
          return
        }
      }
      // Server-side resolve-defaults fills in things like next-sequence /
      // computed defaults that aren't in the static metadata. Non-fatal.
      let serverDefaults: Record<string, unknown> = {}
      try {
        const def = await rt.resolveDefaults(project, res.obj_name)
        if (def?.values) serverDefaults = def.values
      } catch {
        // ignore — manual defaults still apply
      }
      setAddState({
        kind: 'open',
        sourceObjName: res.obj_name,
        sm,
        values: { ...serverDefaults, ...initial },
        errors: {},
        banner: null,
        saving: false,
      })
    } catch (err) {
      const { message } = parseApiError(err)
      setAddState({ kind: 'unsupported', reason: message })
    }
  }, [project, spec, addTarget, addLabel])

  // Which saved-row column to bind back into the field after an Add. Table
  // mode uses the spec's data column; SQL mode uses the configured value
  // column when it's a name (a positional reference can't be mapped to a
  // saved column, so auto-select is skipped and the user picks from the
  // refreshed list).
  const addDataKey = (
    spec?.data
    || (dataCol && !/^\d+$/.test(dataCol.trim()) ? dataCol.trim() : '')
    || ''
  ).trim()

  const saveAdd = useCallback(async () => {
    if (addState.kind !== 'open') return
    setAddState((prev) => prev.kind === 'open' ? { ...prev, saving: true, errors: {}, banner: null } : prev)
    try {
      const res = await rt.save(
        project, addState.sourceObjName, addState.values, undefined,
      )
      rt.notify.success(`Added ${addLabel}`)
      // Auto-select: the save response carries the saved row in `header`,
      // so we can hand it back to the parent FormRenderer without waiting
      // for the lookup refetch. ``addDataKey`` tells us which saved field
      // is the value to bind.
      const savedHeader = (res.header || {}) as Record<string, unknown>
      const dataValRaw = addDataKey
        ? (savedHeader[addDataKey.toLowerCase()] ?? savedHeader[addDataKey])
        : undefined
      const displayRaw = spec ? pickDisplayValue(savedHeader, lookup) : null
      if (dataValRaw !== undefined && dataValRaw !== null) {
        onSelect({
          data: dataValRaw as string | number | null,
          display: displayRaw ?? String(dataValRaw),
        })
        // Tear down the modal entirely — the parent field is now set,
        // there's no reason to leave the lookup picker open.
        onClose()
        return
      }
      // Fallback: refresh the list and let the user pick the new row
      // manually. Shouldn't fire in practice — save always returns the
      // saved row — but guards against future contract drift.
      setAddState({ kind: 'idle' })
      await fetchRows(query)
    } catch (err) {
      const { message, validationErrors } = parseApiError(err)
      const fmap: Record<string, string> = {}
      const general: string[] = []
      for (const it of validationErrors) {
        if (it.field && it.field !== '_record') fmap[it.field] = it.message
        else general.push(it.message)
      }
      setAddState((prev) => prev.kind === 'open' ? {
        ...prev,
        saving: false,
        errors: fmap,
        banner: general.length > 0 ? general.join(' · ') : (Object.keys(fmap).length === 0 ? message : null),
      } : prev)
      if (general[0]) rt.notify.error(general[0])
      else if (Object.keys(fmap).length > 0) rt.notify.error(Object.values(fmap)[0])
      else rt.notify.error(message)
      // Scroll the first errored field into view, mirroring EmbedLivePreview.
      const first = Object.keys(fmap)[0]
      if (first) {
        window.setTimeout(() => {
          const el = document.getElementById(fieldAnchorId(first))
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            const input = el.querySelector('input, select, textarea') as HTMLElement | null
            if (input) input.focus()
          }
        }, 50)
      }
    }
  }, [addState, fetchRows, lookup, onClose, onSelect, project, query, spec, addLabel, addDataKey])

  const onAddFieldChange = useCallback((dbName: string, value: unknown) => {
    setAddState((prev) => {
      if (prev.kind !== 'open') return prev
      const nextErrors = { ...prev.errors }
      delete nextErrors[dbName]
      return {
        ...prev,
        values: { ...prev.values, [dbName]: value },
        errors: nextErrors,
      }
    })
  }, [])

  if (!open) return null

  // Determines what to show in the inline Add panel — gated on parsed
  // spec because we can't open a sub-form for a malformed lookup — unless an
  // explicit add-object is configured, which doesn't need the spec at all.
  const canAdd = allowAdd && !!(addTarget || spec) && addState.kind !== 'unsupported'
  const windowTitle = (title || '').trim() || `Select ${fieldLabel}`

  return (
    <div
      onClick={onClose}
      style={{
        // Above the record editor it is opened from — see `layers`.
        position: 'fixed', inset: 0, zIndex: Z_LOOKUP,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        ref={cardRef}
        onClick={(e) => e.stopPropagation()}
        data-tw-dialog="lookup"
        style={{
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          width: 'min(720px, 100%)',
          maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', borderBottom: '1px solid var(--color-border)',
        }}>
          <div>
            <div style={{ fontSize: '0.9625rem', fontWeight: 600 }}>
              {windowTitle}
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              <code>{(lookup || '').trim() || (sqlMode ? 'Advanced SQL query' : '')}</code>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} title="Close" {...tw('action', 'close')}>
            <X size={16} />
          </button>
        </div>

        <div style={{
          padding: '10px 16px', borderBottom: '1px solid var(--color-border)',
          display: 'flex', gap: 8, alignItems: 'center',
        }}>
          <Search size={14} color="var(--color-text-secondary)" />
          <input
            ref={inputRef}
            {...tw('field', '_lookup_search')}
            type="text"
            value={query}
            placeholder="Search…  (↑/↓ to move · Enter to select · Ctrl+F to search)"
            onChange={(e) => setQuery(e.target.value)}
            style={{
              flex: 1, padding: '7px 10px', borderRadius: 4,
              border: '1px solid var(--color-border)',
              background: 'var(--color-bg-secondary)',
              color: 'var(--color-text)', fontSize: '0.9625rem', outline: 'none',
            }}
          />
          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
            {busy ? 'Loading…' : `${total} row${total === 1 ? '' : 's'}`}
          </span>
          {canAdd && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={startAdd}
              {...tw('action', 'lookup_add')}
              disabled={addState.kind === 'resolving' || addState.kind === 'open'}
              title={`Add a new ${addLabel} record`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Plus size={13} />
              {addState.kind === 'resolving' ? 'Resolving…' : 'Add'}
            </button>
          )}
        </div>

        {addState.kind === 'unsupported' && (
          <div style={{
            padding: '8px 16px',
            background: 'rgba(245,158,11,0.12)',
            borderBottom: '1px solid var(--color-border)',
            color: '#92400e',
            fontSize: '0.9125rem',
          }}>
            Can't add new {addLabel} from here: {addState.reason}
          </div>
        )}

        {addState.kind === 'open' && (
          <div style={{
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 16px',
              borderBottom: '1px solid var(--color-border)',
              fontSize: '0.9125rem',
            }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Plus size={14} color="var(--color-accent)" />
                <strong>New {addLabel}</strong>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  · saves and selects automatically
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setAddState({ kind: 'idle' })}
                  {...tw('action', 'cancel')}
                  disabled={addState.saving}
                >Cancel</button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={saveAdd}
                  {...tw('action', 'save')}
                  disabled={addState.saving}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <Save size={13} />
                  {addState.saving ? 'Saving…' : 'Save & select'}
                </button>
              </div>
            </div>
            {addState.banner && (
              <div style={{
                padding: '6px 16px', fontSize: '0.875rem',
                color: '#b91c1c', background: 'rgba(239,68,68,0.10)',
              }}>
                {addState.banner}
              </div>
            )}
            <div style={{
              padding: '10px 16px',
              maxHeight: '40vh',
              overflow: 'auto',
              background: 'var(--color-bg)',
            }}>
              <FormRenderer
                model={addState.sm}
                values={addState.values}
                errors={addState.errors}
                onChange={onAddFieldChange}
                readOnlyKey={false}
                project={project}
                objName={addState.sourceObjName}
              />
            </div>
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto' }}>
          {error ? (
            <div style={{ padding: 24, color: '#ef4444', fontSize: '0.9375rem' }}>
              {error}
            </div>
          ) : rows.length === 0 && !busy ? (
            <div style={{
              padding: 40, textAlign: 'center', fontSize: '0.9625rem',
              color: 'var(--color-text-secondary)',
            }}>
              No rows found.
            </div>
          ) : columns.length > 1 ? (
            // Multi-column SQL lookup — show every selected column; the value
            // column is flagged in its header so the user knows what gets
            // stored when they click a row.
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9375rem' }}>
              <thead style={{ position: 'sticky', top: 0, background: 'var(--color-bg)' }}>
                <tr>
                  {multi && <th style={{ ...th, width: 34 }} aria-label="Selected" />}
                  {columns.map((c) => (
                    <th key={c} style={th}>
                      {humanizeCol(c)}
                      {c === dataKey && (
                        <span style={{ color: 'var(--color-accent)', marginLeft: 4 }}>•value</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={`${String(r.data)}-${i}`}
                    data-lookup-row={i}
                    onClick={() => pickRow(r)}
                    {...tw('row', i, { key: String(r.data ?? '') })}
                    onMouseEnter={() => setActiveIndex(i)}
                    style={{
                      borderBottom: '1px solid var(--color-border)',
                      cursor: 'pointer',
                      background: i === activeIndex ? 'var(--color-bg-secondary)' : 'transparent',
                    }}
                  >
                    {multi && (
                      <td style={{ ...td, width: 34 }}>
                        <input type="checkbox" checked={isTicked(r)} readOnly />
                      </td>
                    )}
                    {columns.map((c) => {
                      const v = (r.cells || {})[c]
                      const isVal = c === dataKey
                      return (
                        <td
                          key={c}
                          style={isVal
                            ? { ...td, color: 'var(--color-text-secondary)', fontFamily: 'monospace' }
                            : td}
                        >
                          {v === null || v === undefined ? '—' : String(v)}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9375rem' }}>
              <thead style={{ position: 'sticky', top: 0, background: 'var(--color-bg)' }}>
                <tr>
                  {multi && <th style={{ ...th, width: 34 }} aria-label="Selected" />}
                  <th style={th}>Display</th>
                  <th style={{ ...th, width: '40%' }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={`${String(r.data)}-${i}`}
                    data-lookup-row={i}
                    onClick={() => pickRow(r)}
                    {...tw('row', i, { key: String(r.data ?? '') })}
                    onMouseEnter={() => setActiveIndex(i)}
                    style={{
                      borderBottom: '1px solid var(--color-border)',
                      cursor: 'pointer',
                      background: i === activeIndex ? 'var(--color-bg-secondary)' : 'transparent',
                    }}
                  >
                    {multi && (
                      <td style={{ ...td, width: 34 }}>
                        <input type="checkbox" checked={isTicked(r)} readOnly />
                      </td>
                    )}
                    <td style={td}>
                      {r.display}
                      {r.semantic && (
                        // The user did not type anything that matches this row
                        // literally — saying so is what stops a close guess
                        // from reading as an exact hit.
                        <span
                          title="Closest match — found by meaning, not by the text you typed"
                          style={{
                            marginLeft: 6, padding: '1px 6px', borderRadius: 999,
                            fontSize: 13, verticalAlign: 'middle',
                            background: 'var(--color-bg-secondary, #f1f5f9)',
                            border: '1px solid var(--color-border)',
                            color: 'var(--color-text-secondary)',
                          }}
                        >
                          closest match
                        </span>
                      )}
                    </td>
                    <td style={{ ...td, color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>
                      {r.data === null || r.data === undefined ? '—' : String(r.data)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {multi && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 8, padding: '10px 16px',
            borderTop: '1px solid var(--color-border)',
          }}>
            <span style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
              {multiSel.size} selected · click rows to toggle
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} {...tw('action', 'cancel')}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary btn-sm" onClick={applyMulti} {...tw('action', 'apply')}>
                Apply{multiSel.size > 0 ? ` (${multiSel.size})` : ''}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Best-effort display text for an auto-selected just-saved row. We try
// the DISPLAY column from the lookup spec first (so the chip text
// matches what the user would see for any other selection), and fall
// back to the raw data value when that column isn't on the saved row.
function pickDisplayValue(header: Record<string, unknown>, lookupSpec: string): string | null {
  const m = (lookupSpec || '').trim().match(
    /^([A-Za-z_][A-Za-z0-9_]*)\s*\(\s*([A-Za-z_][A-Za-z0-9_]*)\s*,\s*([A-Za-z_][A-Za-z0-9_]*)\s*\)\s*$/,
  )
  if (!m) return null
  const dispCol = m[2]
  const v = header[dispCol.toLowerCase()] ?? header[dispCol]
  if (v === undefined || v === null || v === '') return null
  return String(v)
}

// Title-case a raw SQL output column name for a grid header
// (e.g. ``state_name`` → ``State Name``, ``display`` → ``Display``).
function humanizeCol(name: string): string {
  return (name || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim() || name
}

const th: React.CSSProperties = {
  padding: '8px 12px', textAlign: 'left',
  borderBottom: '1px solid var(--color-border)',
  fontWeight: 600, fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  letterSpacing: 0.3, textTransform: 'uppercase',
}

const td: React.CSSProperties = {
  padding: '8px 12px',
  color: 'var(--color-text)',
}
